import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  fetchMyPharmacy,
  type MyPharmacyResponse,
  type TranscriptionMode,
} from '@/shared/apis/auth'

type UseMyPharmacyQueryReturn = {
  data: MyPharmacyResponse | undefined
  transcriptionMode: TranscriptionMode
  isLoading: boolean
  error: Error | null
}

/**
 * GET /api/me/pharmacy/ を読む hook。
 *
 * `transcription_mode` は streaming / batch 分岐の dispatcher 判定に使う。
 * レスポンス待ち or 失敗時は `'batch'` にフォールバックして既存経路で動かす
 * (streaming は薬局単位 opt-in のため、未確認状態では既存動作を壊さない)。
 *
 * 設計: yakureki-back/log/143/design/stt_architecture.md §7.7
 */
export const useMyPharmacyQuery = (): UseMyPharmacyQueryReturn => {
  const query = useQuery({
    queryKey: queryKeys.pharmacy.me(),
    queryFn: fetchMyPharmacy,
    staleTime: 1000 * 60 * 10, // 10 分キャッシュ (薬局設定は頻繁に変わらない)
  })

  // 経路切替の診断用: transcription_mode の取得値を初回 fetch / 値変化のたびに log。
  const lastLoggedRef = useRef<TranscriptionMode | undefined>(undefined)
  useEffect(() => {
    const mode = query.data?.transcription_mode
    if (mode && lastLoggedRef.current !== mode) {
      lastLoggedRef.current = mode
      console.info(
        '[transcription-dispatch] /api/me/pharmacy/ fetched transcription_mode=',
        mode
      )
    }
  }, [query.data?.transcription_mode])

  return {
    data: query.data,
    transcriptionMode: query.data?.transcription_mode ?? 'batch',
    isLoading: query.isLoading,
    error: query.error ?? null,
  }
}
